import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.tsx';
import { SidebarStory, apiDelete } from '../services/api.ts';


interface SidebarProps {
  stories: SidebarStory[];
  currentStoryId?: string;                 // string id is fine
  onStoryChange?: (storyId: string) => void;
  onStoryDelete?: () => void;              // callback to refresh stories after deletion
  loading?: boolean;                       // only for initial sidebar stories fetch
  switching?: boolean;                     // not used anymore; kept optional for compatibility
}

// Helper for status emoji in option text (native select doesn't support JSX)
const statusEmoji = (s?: string) => {
  if (!s) return '';
  const done = s === 'completed' || s === 'ready' || s === 'done';
  return done ? ' ✓' : ' ⏳';
};

const Sidebar: React.FC<SidebarProps> = ({
  stories,
  currentStoryId,
  onStoryChange,
  onStoryDelete,
  loading = false,
  switching = false,
}) => {
  // persist open/closed across refreshes
  const [isOpen, setIsOpen] = useState<boolean>(() => {
    try {
      const saved = localStorage.getItem('sidebarOpen');
      return saved === null ? true : saved === 'true';
    } catch {
      return true;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem('sidebarOpen', String(isOpen));
    } catch {}
  }, [isOpen]);

  const navigate = useNavigate();
  const { user, logout } = useAuth();
  
  // Delete story state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // sort stories by creation date (newest first)
  const storiesSorted = useMemo(
    () => [...stories].sort((a, b) => +new Date(b.created_at) - +new Date(a.created_at)),
    [stories]
  );

  // ensure select never renders blank when currentStoryId is falsy or stale
  const storyIds = useMemo(() => storiesSorted.map(s => String(s.id)), [storiesSorted]);
  const hasCurrent = currentStoryId ? storyIds.includes(currentStoryId) : false;
  const selectedId = hasCurrent ? currentStoryId! : '';

  // Find the currently selected story for status pill
  const current = useMemo(
    () => storiesSorted.find(s => String(s.id) === selectedId),
    [storiesSorted, selectedId]
  );

  const handleChange = (id: string) => {
    if (!id || id === currentStoryId) return;  // selection guard
    if (onStoryChange) onStoryChange(id);
    // auto-close on mobile after selection (optional quality-of-life)
    if (window.innerWidth < 1024) setIsOpen(false);
  };

  const handleDeleteStory = async () => {
    if (!currentStoryId || !current) return;
    
    setIsDeleting(true);
    try {
      await apiDelete(`/runs/${currentStoryId}`);
      setShowDeleteConfirm(false);
      // Refresh stories list first
      if (onStoryDelete) onStoryDelete();
      // Don't navigate - stay on current page
    } catch (error) {
      console.error('Failed to delete story:', error);
      alert('Failed to delete story. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      {/* Sidebar */}
      <aside
        id="app-sidebar"
        className={`
          fixed left-0 top-0 h-full bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 shadow-2xl border-r border-slate-700 z-30
          transition-all duration-300 ease-in-out backdrop-blur-sm
          ${isOpen ? 'w-64' : 'w-0 overflow-hidden'}
        `}
        aria-hidden={!isOpen}
      >
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="p-6 border-b border-slate-700">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-sm font-bold">SB</span>
              </div>
              <h2 className="text-lg font-semibold text-white">Workshop</h2>
            </div>
          </div>

          {/* Navigation */}
          <div className="p-6 border-b border-slate-700">
            <h3 className="text-sm font-medium text-slate-300 mb-3">Navigation</h3>
            <nav className="space-y-2">
              <button
                onClick={() => navigate('/home')}
                className="w-full flex items-center space-x-3 px-4 py-3 bg-slate-700 hover:bg-slate-600 rounded-xl text-left transition-all duration-200 hover:scale-105"
              >
                <span className="text-lg">🏠</span>
                <span className="font-medium text-white">Home</span>
              </button>
            </nav>
          </div>

          {/* Story Selector */}
          <div className="p-6 border-b border-slate-700">
            {loading ? (
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500 mx-auto mb-3" />
                <p className="text-sm text-slate-400">Loading stories...</p>
              </div>
            ) : (
              <>
                <label
                  htmlFor="story-select"
                  className="block text-sm font-medium text-slate-300 mb-3"
                >
                  Current Story
                </label>
                <div className="relative">
                  <div className="relative w-full">
                    <select
                      id="story-select"
                      value={selectedId}
                      onChange={(e) => handleChange(e.target.value)}
                      className="w-full bg-slate-700 border border-slate-500 hover:bg-slate-650 text-white rounded-xl sm:py-3 py-2.5 px-4 pr-9 truncate leading-5 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent shadow-sm transition-all duration-200 appearance-none cursor-pointer"
                      aria-label="Select current story"
                    >
                      {/* Placeholder if no current story is selected */}
                      {!selectedId && (
                        <option value="" disabled>
                          {storiesSorted.length ? 'Select a story…' : 'No stories yet'}
                        </option>
                      )}

                      {storiesSorted.map((story) => (
                        <option
                          key={story.id}
                          value={story.id.toString()}
                          className="bg-slate-700 text-white"
                        >
                          {`${statusEmoji(story.status)} ${(story.story_name || story.child_name || `Story ${story.id}`)}`}
                        </option>
                      ))}
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                    {/* subtle gradient to hint dropdown area */}
                    <div className="pointer-events-none absolute inset-y-0 right-8 w-6 bg-gradient-to-l from-slate-700/60 to-transparent" />
                  </div>
                </div>
                
                {/* Status + Delete row */}
                {current && (
                  <div className="mt-2 flex items-center justify-between">
                    <span className={
                      `inline-block px-2 py-0.5 rounded-full text-xs
                       ${!current.status ? 'bg-slate-200 text-slate-700' :
                         (current.status === 'completed' || current.status === 'ready')
                           ? 'bg-green-100 text-green-800'
                           : 'bg-yellow-100 text-yellow-800'}`
                    }>
                      {current.status || 'unknown'}
                    </span>

                    {/* Delete button moved under dropdown */}
                    {currentStoryId && (
                      <button
                        onClick={() => setShowDeleteConfirm(true)}
                        className="ml-3 px-2 py-1 bg-red-600 hover:bg-red-700 border border-red-500 rounded-lg text-white transition-colors text-xs"
                        title="Delete story"
                        aria-label="Delete story"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Create New Story Button */}
          <div className="p-6 border-b border-slate-700">
            <button
              onClick={() => navigate('/story-setup')}
              className="w-full flex items-center space-x-3 px-4 py-3 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 rounded-xl text-left transition-all duration-200 hover:scale-105"
            >
              <span className="text-lg">✨</span>
              <span className="font-medium text-white">Create New Story</span>
            </button>
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-slate-700 mt-auto">
            <div className="bg-slate-800 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-xs font-bold">
                      {user?.name?.charAt(0) || 'U'}
                    </span>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-white">
                      {user?.name || 'User'}
                    </div>
                    <div className="text-xs text-slate-400">Welcome back!</div>
                  </div>
                </div>
                <button
                  onClick={logout}
                  className="text-slate-400 hover:text-white transition-colors p-1"
                  title="Logout"
                  aria-label="Logout"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Sidebar Toggle Button */}
      <button
        onClick={() => setIsOpen(o => !o)}
        className="fixed left-4 top-4 z-40 p-3 bg-gradient-to-r from-slate-800 to-slate-900 shadow-xl border border-slate-700 rounded-xl hover:from-slate-700 hover:to-slate-800 transition-all duration-200 hover:scale-105 backdrop-blur-sm"
        aria-controls="app-sidebar"
        aria-expanded={isOpen}
        aria-label="Toggle sidebar"
        title="Toggle sidebar"
      >
        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>

      {/* Overlay when sidebar is open on mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-25 z-20 lg:hidden"
          onClick={() => setIsOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-slate-800 rounded-xl p-6 max-w-md w-full border border-slate-700">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-white">Delete Story</h3>
            </div>
            
            <p className="text-slate-300 mb-6">
              Are you sure you want to delete <strong className="text-white">
                {current?.story_name || current?.child_name || `Story ${current?.id}`}
              </strong>? This action cannot be undone.
            </p>
            
            <div className="flex space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 px-4 py-2 bg-slate-600 hover:bg-slate-700 text-white rounded-lg transition-colors"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteStory}
                disabled={isDeleting}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              >
                {isDeleting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Deleting...</span>
                  </>
                ) : (
                  <span>Delete</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Sidebar;
